/* Oshea Smart Search — predictive search controller
 * Drop-in replacement for the theme's search.js (same custom element + public behaviour)
 * Adds: discovery panel, recent searches, rotating placeholder, keyboard navigation,
 * request cancelling, wider field matching (tags / type / SKU), analytics events.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.activeIndex = -1;
    this.recentKey = 'oshea_recent_searches';
    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.focusElement = this.input;
    this.form = this.querySelector('form');
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.isHeaderSearch = this.closest('header') !== null;

    const debounce =
      window.FoxTheme && FoxTheme.utils && FoxTheme.utils.debounce
        ? FoxTheme.utils.debounce
        : (fn, wait) => {
            let t;
            return (...args) => {
              clearTimeout(t);
              t = setTimeout(() => fn(...args), wait);
            };
          };

    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', debounce(this.onChange.bind(this), 220));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('click', this.onPanelClick.bind(this));
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove(this.states.OPEN);
      document.body.classList.remove(this.states.SEARCH_OPEN);
    });

    this.renderRecent();
    this.initPlaceholder();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }
  get sectionId() {
    return window.FoxTheme && FoxTheme.utils && FoxTheme.utils.getSectionId
      ? FoxTheme.utils.getSectionId(this)
      : this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  /* ---------- open / focus ---------- */
  onFocus() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`
      );
    }
    this.renderRecent();
    this.searchContent?.classList.remove('hidden');
    this.toggleSearchState(true);

    if (this.getQuery().length === 0) {
      this.removeAttribute('results');
      return;
    }
    this.renderSection(this.setupURL().toString());
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.classList.remove('has-value');
    this.removeAttribute('results');
    this.resetActive();
    this.input.focus();
  }

  handleProductTypeChange() {
    if (this.getQuery().length > 0) this.renderSection(this.setupURL().toString());
  }

  setupURL() {
    const root = (window.shopUrl || window.location.origin).replace(/\/$/, '');
    const route =
      (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.predictive_search_url) ||
      `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}search/suggest`;
    const url = new URL(`${root}${route}`);

    let term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value !== '') {
      term = `product_type:${this.searchProductTypes.value} AND ${term}`;
    }
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,query,article,page');
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 6);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,tag,vendor,variants.title,variants.sku');
    url.searchParams.set('section_id', this.sectionId);
    return url;
  }

  onChange() {
    this.resetActive();
    if (this.getQuery().length === 0) {
      this.abortController?.abort();
      this.removeAttribute('results');
      this.renderRecent();
      return;
    }
    this.renderSection(this.setupURL().toString());
  }

  /* ---------- fetching ---------- */
  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.renderSearchResults(this.cachedMap.get(url));
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setLoadingState(true);

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.cachedMap.set(url, responseText);
        if (this.getQuery().length === 0) return;
        this.renderSearchResults(responseText);
        this.setAttribute('results', 'true');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Search error: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + this.sectionId;
    const targetElement = document.getElementById(id);
    if (!targetElement) return;

    const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);
    if (!contentElement) return;

    targetElement.innerHTML = contentElement.innerHTML;
    this.searchContent?.classList.remove('hidden');
    this.toggleSearchState(true);
    this.resetActive();
    this.searchContent && (this.searchContent.scrollTop = 0);

    const count = targetElement.querySelector('[data-os-results-count]');
    if (!count && targetElement.querySelector('.os-empty')) {
      this.track('search_no_results', { search_term: this.getQuery() });
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
    }
  }

  /* ---------- open / close ---------- */
  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);
    if (shouldClose) setTimeout(() => this.toggleSearchState(false));
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    this.input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen) this.resetActive();
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  /* ---------- keyboard ---------- */
  getOptions() {
    if (!this.searchContent) return [];
    return Array.from(this.searchContent.querySelectorAll('[data-os-option]')).filter(
      (el) => el.offsetParent !== null
    );
  }

  resetActive() {
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.searchContent?.querySelectorAll('[data-os-option].is-active').forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.toggleSearchState(false);
      this.input.blur();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;

    const options = this.getOptions();
    if (event.key === 'Enter') {
      if (this.activeIndex > -1 && options[this.activeIndex]) {
        event.preventDefault();
        options[this.activeIndex].click();
      }
      return;
    }
    if (!options.length) return;
    event.preventDefault();

    this.activeIndex =
      event.key === 'ArrowDown'
        ? (this.activeIndex + 1) % options.length
        : (this.activeIndex - 1 + options.length) % options.length;

    options.forEach((option, i) => {
      const on = i === this.activeIndex;
      if (!option.id) option.id = `os-opt-${this.sectionId}-${i}`;
      option.classList.toggle('is-active', on);
      option.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /* ---------- clicks, submit, recent ---------- */
  onPanelClick(event) {
    const clearBtn = event.target.closest('[data-os-recent-clear]');
    if (clearBtn) {
      event.preventDefault();
      this.saveRecentList([]);
      this.renderRecent();
      this.input.focus();
      return;
    }

    const option = event.target.closest('[data-os-option]');
    if (!option || !this.contains(option)) return;

    const type = option.dataset.osType || 'link';
    const term = option.dataset.osTerm || this.getQuery();

    if (type === 'view_all') {
      return; // handled by form submit
    }
    if (term) this.addRecent(term);

    this.track('search_select', {
      search_term: this.getQuery() || term,
      select_type: type,
      item_name: option.dataset.osProduct || option.textContent.trim().slice(0, 80),
      item_id: option.dataset.osProductId || undefined,
      source: option.dataset.osSource || undefined,
    });
  }

  onSubmit(event) {
    const q = this.getQuery();
    if (!q) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    this.addRecent(q);
    this.track('search', { search_term: q });
  }

  getRecentList() {
    try {
      const list = JSON.parse(localStorage.getItem(this.recentKey) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  saveRecentList(list) {
    try {
      localStorage.setItem(this.recentKey, JSON.stringify(list));
    } catch (e) {}
  }

  addRecent(term) {
    const clean = String(term).trim().slice(0, 60);
    if (clean.length < 2) return;
    const list = this.getRecentList().filter((t) => t.toLowerCase() !== clean.toLowerCase());
    list.unshift(clean);
    this.saveRecentList(list.slice(0, 6));
  }

  renderRecent() {
    const wrap = this.querySelector('[data-os-recent]');
    const listEl = this.querySelector('[data-os-recent-list]');
    if (!wrap || !listEl) return;

    const list = this.getRecentList();
    listEl.textContent = '';
    wrap.hidden = list.length === 0;
    if (!list.length) return;

    const action = this.form ? this.form.getAttribute('action') : '/search';
    list.forEach((term) => {
      const a = document.createElement('a');
      a.className = 'os-chip os-chip--recent';
      a.href = `${action}?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(term)}`;
      a.setAttribute('data-os-option', '');
      a.dataset.osTerm = term;
      a.dataset.osType = 'recent';
      a.innerHTML =
        '<svg class="os-ico" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
      const span = document.createElement('span');
      span.textContent = term;
      a.appendChild(span);
      listEl.appendChild(a);
    });
  }

  /* ---------- animated placeholder (slide-up word) ---------- */
  initPlaceholder() {
    const ph = this.querySelector('[data-os-ph]');
    const word = this.querySelector('[data-os-ph-word]');
    const syncValue = () => this.classList.toggle('has-value', this.input.value.length > 0);
    this.input.addEventListener('input', syncValue);
    syncValue();

    const terms = (this.dataset.placeholders || '')
      .split('|')
      .map((t) => t.trim())
      .filter(Boolean);
    if (!ph || !word || !terms.length) return;

    this.positionPlaceholder();
    window.addEventListener('resize', () => this.positionPlaceholder(), { passive: true });
    this.searchProductTypes?.addEventListener('change', () => this.positionPlaceholder());
    this.input.addEventListener('focus', () => this.positionPlaceholder());
    this.classList.add('os-ph-ready');

    const quote = (t) => `“${t}”`;
    word.textContent = quote(terms[0]);
    if (terms.length < 2) return;

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* No animation wanted (or possible): swap the text in place. */
    if (reduceMotion || typeof word.animate !== 'function') {
      let n = 0;
      this.placeholderTimer = setInterval(() => {
        if (this.input.value || document.hidden) return;
        n = (n + 1) % terms.length;
        word.textContent = quote(terms[n]);
      }, 2600);
      return;
    }

    /* Every term is rendered once into a strip, and a rotation just moves the
     * strip with a transform - compositor work, no layout. The earlier version
     * rewrote the word and restarted a CSS transition with a forced reflow,
     * which cost 45-110ms of main-thread time every 2.6s, on every page, for
     * as long as the page stayed open. Same animation, no main-thread cost. */
    const strip = document.createElement('span');
    strip.className = 'os-ph__strip';
    strip.style.display = 'block';
    strip.style.willChange = 'transform';
    terms.concat(terms[0]).forEach((term) => {
      const line = document.createElement('span');
      line.style.display = 'block';
      line.style.lineHeight = '1.3em';
      line.style.whiteSpace = 'nowrap';
      line.textContent = quote(term);
      strip.appendChild(line);
    });
    word.style.display = 'none';
    word.parentNode.appendChild(strip);

    let lineHeight = 0;
    const measure = () => {
      lineHeight = strip.firstElementChild ? strip.firstElementChild.getBoundingClientRect().height : 0;
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });

    let i = 0;
    this.placeholderTimer = setInterval(() => {
      if (this.input.value || document.hidden || !lineHeight) return;
      const from = -lineHeight * i;
      i += 1;
      const anim = strip.animate(
        [{ transform: `translateY(${from}px)` }, { transform: `translateY(${-lineHeight * i}px)` }],
        { duration: 380, easing: 'cubic-bezier(0.25, 1, 0.5, 1)', fill: 'forwards' }
      );
      strip.getAnimations().forEach((a) => a !== anim && a.cancel());
      /* The last line is a copy of the first, so the jump back is invisible. */
      if (i === terms.length) {
        anim.finished
          .then(() => {
            i = 0;
            anim.cancel();
            strip.style.transform = 'translateY(0)';
          })
          .catch(() => {});
      }
    }, 2600);
  }

  positionPlaceholder() {
    const ph = this.querySelector('[data-os-ph]');
    if (!ph) return;
    const cs = getComputedStyle(this.input);
    const left = this.input.offsetLeft + parseFloat(cs.paddingLeft || 0) + parseFloat(cs.borderLeftWidth || 0);
    ph.style.setProperty('--os-ph-left', `${left}px`);
  }

  /* ---------- analytics (GTM dataLayer + Shopify customer events) ---------- */
  track(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (e) {}
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    clearInterval(this.placeholderTimer);
  }
}

/* Upgrade off the critical path.
 *
 * Defining the element immediately ran the whole controller (event wiring,
 * recent searches, the rotating placeholder and its layout reads) before the
 * page had painted - ~300ms of blocking work on the home page, where most
 * visitors never touch search. It now starts when the browser is idle, or at
 * once if the shopper reaches the search field first, so nothing is lost: the
 * placeholder animation just begins a moment later.
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const events = ['pointerdown', 'touchstart', 'keydown', 'focusin'];
  let started = false;

  const start = () => {
    if (started) return;
    started = true;
    events.forEach((type) => document.removeEventListener(type, start, true));

    customElements.define('predictive-search', PredictiveSearch);

    // If the shopper was already focusing the field when we upgraded, the
    // element missed that focus event - open the panel for them.
    setTimeout(() => {
      const active = document.activeElement;
      const host = active && active.closest && active.closest('predictive-search');
      if (host && typeof host.onFocus === 'function') host.onFocus();
    });
  };

  events.forEach((type) => document.addEventListener(type, start, { capture: true, passive: true }));

  if ('requestIdleCallback' in window) {
    requestIdleCallback(start, { timeout: 2500 });
  } else {
    setTimeout(start, 1200);
  }
})();
